import { useEffect, useRef, useState } from 'preact/hooks'
import Price from '../price/price'
import QuantityInput from '../form/quantity-input/quantity-input'
import Image from '../image/image'
import CarouselSwiper from '@/components/modules/carousel-swiper/carousel-swiper'
import Icon from '../icon/icon'
import { giftWrapSelectors, on, select } from '@/components/helpers/dom'
import Textarea from '@/components/snippets/form/text-input/textarea'
import CheckboxInput from '../form/checkbox-input/checkbox-input'
import ProductATC from '../product/product-atc/product-atc'
import CustomModalDialog from '../custom-modal-dialog/custom-modal-dialog'
import { emitterProps, globalEmitter } from '@/components/helpers/emitter-event'
import LoaderSpin from '../loader-spin/loader-spin'
import { getMaxQuantity } from '@/components/libs/cart'
import useCartGlobal from '@/components/hooks/useCartGlobal'
/* eslint-disable no-template-curly-in-string */

const deviceHeight = window.screen.height
const isLargeDevice = deviceHeight > 900
const ratioImage = isLargeDevice ? 'aspect-1' : 'md-max:h-[44dvh] md:aspect-1'

const OPTIONS_GIFT_SWIPER = {
  slidesPerView: 3,
  spaceBetween: 12,
  freeMode: {
    enabled: true,
    sticky: true
  },
  centerInsufficientSlides: false,
  resistanceRatio: 0.35,
  grabCursor: true,
  speed: 500,
  breakpoints: {
    768: {
      slidesPerView: 2.3,
      spaceBetween: 12
    }
  }
}

const OPTIONS_CARDS_MESSAGE = {
  slidesPerView: 4.3,
  spaceBetween: 0,
  freeMode: {
    enabled: true,
    sticky: true
  },
  centerInsufficientSlides: false,
  grabCursor: true,
  speed: 500
}

const INIT_STATE = {
  currentItem: null,
  isCartPage: false,
  cards: [],
  currentStep: 0,
  optionsSelected: [],
  options: [],
  quantityProduct: 1,
  isEditModal: false
}

const CDN = GM_STATE.shopify.shop === 'saint-valentine-dev' ? 'https://cdn.shopify.com/s/files/1/0704/2993/3809/files' : 'https://cdn.shopify.com/s/files/1/1025/4627/files'

function GiftWrappingModal() {
  const { productGiftWrapping, giftWrappingMessage } = GM_STATE.product
  const optionsWrapping = productGiftWrapping.metafields.product_gift_wrapping
  const [giftState, setGiftState] = useState(INIT_STATE)
  const [engravingProperties, setEngravingProperties] = useState({})
  const messageRef = useRef()

  const eventModalClose = () => {
    const customModalEl = select(`modal-dialog[data-target="${giftWrapSelectors.MODAL_GIFT_WRAPPING_SELECTOR}"]`)

    if (!customModalEl) return

    on('close-modal', () => {
      setGiftState(INIT_STATE)
    }, customModalEl)
  }

  const updateGiftState = async (customData) => {
    const currentItem = customData?.giftWrappingData?.currentItem
    const isCartPage = customData?.giftWrappingData?.isCartPage
    const isEditModal = customData?.giftWrappingData?.isEditModal
    const itemProperties = customData?.giftWrappingData?.itemProperties || {}

    setEngravingProperties({
      ...engravingProperties,
      _engraving: itemProperties._engraving,
      _timestamp: itemProperties._timestamp
    })

    if (currentItem) {
      const { selected, optionsSorted, messages, quantityProduct } = await syncSelectedOptions(currentItem, isCartPage)

      setGiftState({ ...giftState, currentItem, isCartPage, optionsSelected: selected, options: [...optionsSorted], cards: [...messages], quantityProduct, isEditModal, currentStep: isEditModal ? 4 : 0 })
    }
  }

  const onChangeCard = (value, type = 'number') => {
    if (type === 'number') {
      const cardSize = messageRef.current?.length

      const nCards = value > cardSize
        ? messageRef.current.concat(Array(value - cardSize).fill(1).map(() => ({ message: '', isGiftReceipt: true })))
        : [...messageRef.current].filter((_, index) => index < value)

      setGiftState({ ...giftState, cards: nCards })
    } else {
      setGiftState({ ...giftState, cards: value })
    }
  }

  const onChangeState = (name, value) => {
    // name can be string or object
    if (value === undefined) {
      setGiftState({ ...giftState, ...name })
    } else {
      setGiftState({ ...giftState, [name]: value })
    }
  }

  const syncSelectedOptions = async (currentItem) => {
    const quantityProduct = currentItem?.quantity || 1
    const optionsSorted = [{ type: 'box', data: [] }, { type: 'gifting_sleeve', data: [] }, { type: 'card_design', data: [] }, { type: 'wrapping', data: [] }, { type: 'combo', data: [] }]
    const selected = {}
    const messages = []

    Array.from({ length: quantityProduct }, () => messages.push({ message: '', isGiftReceipt: true }))

    optionsWrapping.forEach(option => {
      const type = option.type.replaceAll(' ', '_').toLowerCase()

      if (!selected[type]) {
        selected[type] = option
      }

      if (type === 'box') optionsSorted[0].data.push(option)
      else if (type === 'gifting_sleeve') optionsSorted[1].data.push(option)
      else if (type === 'card_design') optionsSorted[2].data.push(option)
      else if (type === 'wrapping') optionsSorted[3].data.push(option)
      else if (type === 'combo') optionsSorted[4].data.push(option)
    })

    // initial data gift wrapping
    optionsSorted.filter(option => option.data.length > 0)
    // optionsSorted.push({ type: 'last', data: [] })

    // update data when edit gift wrapping
    if (currentItem?.gift_wrap_product) {
      for (const [key, value] of Object.entries(currentItem.properties)) {
        const isMessage = key.includes('card_message')

        if (isMessage || key.includes('card_gift_receipt')) {
          const lastChar = key.split('_').pop()
          const index = lastChar ? parseInt(lastChar) - 1 : 0

          if (messages[index]) {
            messages[index][isMessage ? 'message' : 'isGiftReceipt'] = value
          } else {
            messages[index] = isMessage ? { message: value } : { isGiftReceipt: value }
          }
        } else {
          const item = optionsSorted.find(i => i.type === key)
          const option = item && item.data.find(i => value === i.name)

          if (option) selected[key] = option
        }
      }
    }
    messageRef.current = messages

    return { optionsSorted, selected, messages, quantityProduct }
  }

  useEffect(() => {
    eventModalClose()
    globalEmitter.on(emitterProps.modal.show, updateGiftState)

    return () => {
      globalEmitter.off(emitterProps.modal.show, updateGiftState)
    }
  }, [])

  return (
    <CustomModalDialog
      id={giftWrapSelectors.MODAL_GIFT_WRAPPING_SELECTOR}
      classMain='w-full sm:max-w-96 md:max-w-106 flex flex-col'
      classHeader='sticky top-0 w-full px-4 py-3.5 text-left border-b modal-dialog-header border-grey-10 font-serif italic text-xl text-default'
      classContent='gift-wrapping-slideout-content flex flex-col flex-1 overflow-hidden'
      content={
        <Content
          giftWrappingMessage={giftWrappingMessage}
          giftState={giftState}
          engravingProperties={engravingProperties}
          onChangeCard={onChangeCard}
          onChangeState={onChangeState}
        />
      }
      type='slideout'
      header={<Header productGiftWrapping={productGiftWrapping} />}
      disableClickOverlay
    />
  )
}

function Header({ productGiftWrapping }) {
  return (
    <div className='flex gap-1'>
      <span>{translate.product.giftWrapping}</span>
      <span>—</span>
      <Price
        price={productGiftWrapping.price}
        classSize='text-xl text-primary'
        hasDecimal
      />
      <span>per item</span>
    </div>
  )
}

function Content({ giftState, onChangeState, giftWrappingMessage, engravingProperties, onChangeCard }) {
  const { currentStep, currentItem, cards, optionsSelected, options, isCartPage, quantityProduct, isEditModal } = giftState
  const maxQuantity = currentItem ? useCartGlobal(currentItem)?.maxQuantity : 1

  return (
    <>
      <HeaderTabs options={options} currentStep={currentStep} quantityProduct={quantityProduct} onChangeState={onChangeState} />
      {options.length > 0
        ? <Steps
          onChangeState={onChangeState}
          options={options}
          currentStep={currentStep}
          optionsSelected={optionsSelected}
          onChangeCard={onChangeCard}
          cards={cards}
          currentItem={currentItem}
          giftWrappingMessage={giftWrappingMessage}
          quantityProduct={quantityProduct}
          maxQuantity={maxQuantity}
          isEditModal={isEditModal}
        />
        : <LoaderSpin />}
      <Footer
        currentStep={currentStep}
        engravingProperties={engravingProperties}
        quantityProduct={quantityProduct}
        cards={cards}
        isLastStep={currentStep === options.length - 1}
        currentItem={currentItem}
        optionsSelected={optionsSelected}
        onChangeState={onChangeState}
        isCartPage={isCartPage}
        onChangeCard={onChangeCard}
      />
    </>
  )
}

function HeaderTabs({ currentStep, options, onChangeState }) {
  const goStep = (step) => {
    onChangeState('currentStep', step)
  }

  return (
    <div className='grid grid-cols-4'>
      {options.map((_, index) => {
        const step = index
        const showCheckIcon = step < currentStep

        if (step < 1) return <></>

        const coditionClass = currentStep === 0 ? 'text-grey-300' : currentStep === index ? 'text-primary  border-b-2 border-primary' : ' text-primary border-b border-grey-100'

        return (
          <div className={`${coditionClass} uppercase text-xs-short py-3 text-center flex gap-2 items-center justify-center cursor-pointer`} onClick={() => goStep(step)}>
            <span>{'step '}{step}</span>
            {showCheckIcon && <Icon name='icon-check' className='w-3 h-3' />}
          </div>
        )
      })}
    </div>
  )
}

function StartScreen({ activeStep, giftWrappingMessage, currentItem, activeOption, quantityProduct, updateQuantityProduct, maxQuantity }) {
  const productImage = currentItem?.featured_image?.url || currentItem?.featured_image
  const productTitle = currentItem?.product_title
  const variantTitle = currentItem?.variant_title === 'Default Title' ? '' : currentItem?.variant_title
  const imageUrl = activeOption?.image
  const video = activeOption?.video
  const useVideo = activeOption?.use_video

  return (
    <div className={`relative flex-col flex-1 overflow-auto ${activeStep ? 'flex' : 'hidden'}`}>
      <div>
        {useVideo
          ? (
            <div
              className={`video-view object-cover pointer-events-none overflow-hidden [&_.is-playing]:opacity-100 ${ratioImage}`}
              dangerouslySetInnerHTML={{
                __html: `
              <video-player
                src="${video?.sources[0]?.url}"
                data-thumbnail="${video?.preview_image?.src}"
                data-play-intersection
                muted
                playsinline
                loop
                auto-init="true"
                class="opacity-0"
                autoplay
              ></video-player>
            `
              }}
            />
            )
          : <Image imageUrl={[imageUrl]} imageClass='fit' className={`${ratioImage} w-full`} alt={`Image ${activeOption?.name}`} key={activeOption?.image} />}
      </div>
      <div className='px-4 py-6 flex flex-col gap-6 flex-1'>
        <div
          className='text-xs text-primary'
          dangerouslySetInnerHTML={{ __html: giftWrappingMessage }}
        />

        <div class='flex items-center gap-3'>
          {productImage &&
            <div class='relative'>
              <Image imageUrl={[productImage]} imageClass='fit' className='aspect-4/5 w-13 relative' alt='Product Image' key={productImage} />
            </div>}
          <div className='flex-1'>
            {productTitle && <p className='text-xs-short uppercase'>{productTitle}</p>}
            {variantTitle && <p className='text-xs-short text-faint uppercase'>{variantTitle}</p>}
          </div>
          <div>
            <QuantityInput
              quantity={quantityProduct}
              min={1}
              max={maxQuantity}
              setQuantity={updateQuantityProduct}
              classWrap='w-24 py-1 px-3'
            />
          </div>
        </div>
      </div>
    </div>
  )
}

function Steps({ currentStep, options, optionsSelected, cards, onChangeCard, onChangeState, giftWrappingMessage, currentItem, quantityProduct, maxQuantity, isEditModal }) {
  let imageURLSelected = ''

  const updateQuantityProduct = (value) => {
    const currentCards = []

    Array.from({ length: value }, () => currentCards.push({ message: '', isGiftReceipt: true }))

    const object = { cards: [...currentCards], quantityProduct: value }

    onChangeState(object)
  }

  return (
    <div className='relative flex flex-col flex-1 overflow-auto'>
      {options.map((option, index) => {
        const activeOption = optionsSelected[option.type] || ''
        const activeStep = currentStep === index

        imageURLSelected += `${optionsSelected[option.type]?.name?.toLowerCase()?.replaceAll(' ', '')}_`

        return (
          <>
            {index < 1
              ? <StartScreen activeStep={activeStep} giftWrappingMessage={giftWrappingMessage} currentItem={currentItem} activeOption={activeOption} maxQuantity={maxQuantity} updateQuantityProduct={updateQuantityProduct} quantityProduct={quantityProduct} />
              : index >= 1 && index < options.length - 1
                ? <Step activeStep={activeStep} option={option} optionsSelected={optionsSelected} imageURLSelected={imageURLSelected} cards={cards} onChangeState={onChangeState} />
                : <LastStep activeStep={activeStep} onChangeCard={onChangeCard} imageURLSelected={imageURLSelected} cards={cards} />}
          </>
        )
      })}

    </div>
  )
}

function Step({ activeStep, option, optionsSelected, onChangeState, imageURLSelected }) {
  const { type, data } = option

  if (!optionsSelected || !optionsSelected[type]) return <div className='flex-1' />

  const selected = optionsSelected[type]
  const imageURL = `${CDN}/${imageURLSelected.slice(0, -1)}.png`

  const onSelect = (item) => {
    const nOptionsSelected = { ...optionsSelected }
    const type = item.type.replaceAll(' ', '_').toLowerCase()

    nOptionsSelected[type] = item

    onChangeState('optionsSelected', nOptionsSelected)
  }

  const children = data.map(item =>
    <swiper-slide onClick={() => onSelect(item)}>
      <div className={`${selected.name === item.name ? 'border border-primary p-2.5' : ''}`}>
        <Image imageUrl={[item?.image]} imageClass='fit' className='aspect-1 w-full cursor-pointer' alt={`Image ${selected.name}`} key={item?.image} />
      </div>
    </swiper-slide>
  )

  return (
    <div className={`flex-1 ${activeStep ? '' : 'hidden'}`}>
      <div class='relative'>
        <Image imageUrl={[imageURL]} imageClass='fit' className={`${ratioImage} w-full relative bg-grey-25`} alt={`Image_${imageURLSelected}`} key={imageURLSelected} />
      </div>

      {data.length > 0 && (
        <div className='pl-4 py-4 md:py-6'>
          <h4 className='font-serif italic text-xl mb-3 md:mb-4'>{translate.product.selectYour}{type.replaceAll('_', ' ')}</h4>
          <CarouselSwiper
            children={children}
            options={OPTIONS_GIFT_SWIPER}
            navigation={false}
            pagination={false}
            scrollbar={false}
            isProductSlide={false}
            selector='js-gift-wrapping'
          />
        </div>
      )}
    </div>
  )
}

function LastStep({ cards, onChangeCard, activeStep, imageURLSelected }) {
  const [currentCard, setCurrentCard] = useState(0)
  const { maxLengthMessageGiftWrapping } = GM_STATE.product

  const onChange = (card, index) => {
    const nCards = [...cards]

    nCards[index] = card
    onChangeCard(nCards, 'message')
  }

  const imageURL = `${CDN}/${imageURLSelected.slice(0, -1)}.png`

  return (
    <div className={!activeStep ? 'hidden' : ''}>
      <div class='relative'>
        <Image imageUrl={[imageURL]} imageClass='fit' className={`${ratioImage} w-full relative bg-grey-25`} alt={`Image_${imageURLSelected}`} key={imageURLSelected} />
      </div>
      <div className='flex-1 px-4 py-4 md:py-6 overflow-hidden'>
        <h4 className='font-serif italic text-xl mb-4'>{translate.product.addYourGift}</h4>
        {cards.length > 1 && <CardTabs cards={cards} currentCard={currentCard} setCurrentCard={setCurrentCard} />}
        {cards.map((card, index) => <MessageBox onChange={onChange} active={currentCard === index} card={card} index={index} maxLength={maxLengthMessageGiftWrapping} />)}
      </div>
    </div>

  )
}

function CardTabs({ currentCard, cards, setCurrentCard }) {
  const children = cards.map((_, index) =>
    <swiper-slide className={`${cards.length < 5 && 'flex-1'} ${currentCard === index ? 'border-b-2 border-primary' : 'border-b border-grey-100'}`} onClick={() => setCurrentCard(index)}>
      <div className='cursor-pointer uppercase text-xs-short py-3 text-center flex gap-2 items-center text-primary justify-center'>
        <span>{'Card '}{index + 1}</span>
      </div>
    </swiper-slide>
  )

  if (cards.length < 5) {
    return (
      <div className='mb-3 flex'>
        {children}
      </div>
    )
  }

  return (
    <div className='mb-3'>
      <CarouselSwiper
        children={children}
        options={OPTIONS_CARDS_MESSAGE}
        navigation
        pagination={false}
        scrollbar={false}
        isProductSlide={false}
        classWrapper='gift-wrapping-cards'
        selector='js-gift-wrapping-cards'
        classNavigation='static'
      />
    </div>
  )
}

function MessageBox({ onChange, active, card, index, maxLength }) {
  const updateCard = (value, type = 'checkbox') => {
    const nCard = { ...card }

    nCard[type === 'message' ? 'message' : 'isGiftReceipt'] = value
    onChange(nCard, index)
  }

  return (
    <div className={`${active ? 'flex-1' : 'hidden'} []`}>
      <Textarea
        className='h-24 message-box'
        name='message'
        placeholder={translate.product.typeMessage}
        label=''
        value={card.message}
        onInput={(e) => updateCard(e.target.value, 'message')}
        maxLength={maxLength}
      />
      <div className='mb-1.5 flex items-center gap-3 mt-4'>
        <CheckboxInput className='!mt-0 w-4' value={card.isGiftReceipt} checked={card.isGiftReceipt} onSelected={(_, isChecked) => updateCard(isChecked)} />
        <p className='text-xs text-grey-800'>{translate.product.addGift}</p>
      </div>
      <p className='text-xs leading-4.5 text-grey-800'>{translate.product.recipientCost}</p>
    </div>
  )
}

function Footer({ currentStep, onChangeCard, quantityProduct, cards, isLastStep, currentItem, optionsSelected, onChangeState, isCartPage, engravingProperties }) {
  const onPrevious = () => {
    onChangeState('currentStep', currentStep - 1)
  }
  const onNextStep = () => {
    onChangeState('currentStep', currentStep + 1)
  }

  const properties = {
    options: optionsSelected,
    messages: cards
  }

  return (
    <div className='border-t border-grey-100 px-3 p-4'>
      {currentStep === 0 && cards.length > 0 && (
        <div>
          <span className='uppercase text-xs-short text-primary'>{translate.product.howManyItems}</span>
          <QuantityInput
            quantity={cards.length}
            min={1}
            max={quantityProduct}
            setQuantity={onChangeCard}
            classWrap='min-h-12 my-3 js-quantity-gift-items border border-grey-200'
            classButton='h-12 w-12'
          />
        </div>
      )}
      <div className={`grid ${currentStep > 0 ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
        {currentStep > 0 && (
          <button className='button-outlined w-full' onClick={onPrevious}>{translate.product.back}</button>
        )}
        {
          isLastStep ? (
            <ProductATC
              currentVariant={currentItem}
              engravingProperties={engravingProperties}
              propertiesGiftWrapping={properties}
              containerClass={'!m-0'}
              isCartPage={isCartPage}
              buttonClass='min-h-12'
              quantityProduct={quantityProduct}
            />
          ) : <button className='button-primary w-full' onClick={onNextStep}>{currentStep > 0 ? translate.product.next : translate.product.selectGift}</button>
        }
      </div >
    </div >
  )
}

export default GiftWrappingModal
